import { createContext, useContext, useRef, useState, useCallback, type ReactNode } from 'react'
import type { TopologyLayout, NodePositionMap } from '../types'

export interface TopologyLayoutState {
  nodePositions: NodePositionMap
  timeRange: string
  expandedServices: string[]
  viewport: { x: number; y: number; zoom: number }
}

interface TopologyHeaderContextType {
  isActive: boolean
  getState: () => TopologyLayoutState | null
  getOnRestore: () => ((layout: TopologyLayout) => void) | null
  register: (
    stateRef: React.RefObject<TopologyLayoutState>,
    onRestore: (layout: TopologyLayout) => void,
  ) => void
  unregister: () => void
}

const TopologyHeaderContext = createContext<TopologyHeaderContextType>({
  isActive: false,
  getState: () => null,
  getOnRestore: () => null,
  register: () => {},
  unregister: () => {},
})

export function TopologyHeaderProvider({ children }: { children: ReactNode }) {
  const [isActive, setIsActive] = useState(false)
  const stateRef = useRef<React.RefObject<TopologyLayoutState> | null>(null)
  const restoreRef = useRef<((layout: TopologyLayout) => void) | null>(null)

  const getState = useCallback(() => stateRef.current?.current ?? null, [])
  const getOnRestore = useCallback(() => restoreRef.current, [])

  const register = useCallback(
    (ref: React.RefObject<TopologyLayoutState>, onRestore: (layout: TopologyLayout) => void) => {
      stateRef.current = ref
      restoreRef.current = onRestore
      setIsActive(true)
    },
    [],
  )

  const unregister = useCallback(() => {
    stateRef.current = null
    restoreRef.current = null
    setIsActive(false)
  }, [])

  return (
    <TopologyHeaderContext.Provider value={{ isActive, getState, getOnRestore, register, unregister }}>
      {children}
    </TopologyHeaderContext.Provider>
  )
}

export function useTopologyHeader() {
  return useContext(TopologyHeaderContext)
}
